/*    
<summary>
  This functional component "AddUpdateCustomerSetting" defines the props like initial values and functions for the child form component.
  Developer:Mohammad Saquib Khan, Created Date:04-June-2024
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { initialPageLimit } from "../../../../constants/pageLimit.constant";
import { useStore } from "../../../../contexts/store-provider";
import IPageInfo from "../../../../models/ICommon";
import { ISorting } from "../../../../models/ISorting";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { getBusinessTypeIdFromLocalStorage } from "../../../../helpers/localstorage-helper";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { formatMessage } from "../../../../translations/format-message";
import TaxListView from "./components/tax-list-view";
import AddTaxSettingForm from "./forms/add-tax-form";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const TaxSettings = (props: IProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState<boolean>(false);
  const { masterManagementStore } = useStore();
  const {
    addUpdateTaxSettingState,
    GetAllTaxService,
    taxSettingState,
    getAllTaxList,
    AddTaxService,
    UpdateTaxService,
    DeleteTaxService,
    GetTaxByIdService,
    deleteTaxSettingState,
    getByIdTaxSettingState,
    getTaxDetail,
    resetGetAllTaxSettingState,
    resetGetByIdTaxSettingState,
    resetdDeleteTaxSettingState,
    resetAddUpdateTaxSettingState,
  } = masterManagementStore;
  // const initialValues: IAddCustomerRate = addCustomerRateState;
  const initialValues = getTaxDetail;

  const { id, modalClosed, showDeletePopup, deletePopupToggleHandler } = props;

  const onModalClosed = useCallback(() => {
    modalClosed();
    resetAddUpdateTaxSettingState();
    resetdDeleteTaxSettingState();
    resetGetAllTaxSettingState();
    resetGetByIdTaxSettingState();
  }, [modalClosed]);

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const handleVisibility = () => {
    setIsDetailsVisible((e) => !e);
  };
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const [sorting, setSorting] = useState<ISorting>({
    column: "userName",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const callGetTaxList = useCallback(() => {
    setDisablePagination(true);
    GetAllTaxService();
    setIsApiCalled(true);
  }, [GetAllTaxService]);

  useEffect(() => {
    setPageInfo((pageInfo) => ({
      ...pageInfo,
      // totalPages: customerRateList?.TotalPages,
      totalPages: 1,
    }));
    setDisablePagination(false);
  }, [getAllTaxList]);
  // }, [getAllTaxList, customerRateList.TotalPages]);

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const onSubmitForm = (values: any) => {
    let finalValues = {
      ...values,
    };
    if (!(values.Id && values.Id > 0)) {
      AddTaxService({
        ...values,
        BusinessTypeId: getBusinessTypeIdFromLocalStorage(),
      });
    } else {
      UpdateTaxService(values.Id, finalValues);
    }
  };

  const deleteHandler = () => {
    DeleteTaxService(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdateTaxSettingState();
  };

  const editHandler = (row: { Id: number }) => {
    setEditableId(row.Id);
    GetTaxByIdService(row.Id);
  };

  useEffect(() => {
    callGetTaxList();
  }, [callGetTaxList]);

  useEffect(() => {
    if (addUpdateTaxSettingState.success) {
      if (editableId > 0) {
        toast.success(formatMessage("tax_setting_update_success"));
      } else {
        toast.success(formatMessage("tax_setting_add_success"));
      }
      callGetTaxList();
      resetAddUpdateTaxSettingState();
      setEditableId(-1);
    }
  }, [
    addUpdateTaxSettingState.success,
    callGetTaxList,
    resetAddUpdateTaxSettingState,
    editableId,
  ]);

  useEffect(() => {
    if (deleteTaxSettingState.success) {
      resetdDeleteTaxSettingState();
      toast.success(formatMessage("tax_setting_delete_success"));
      callGetTaxList();
    }
  }, [
    deleteTaxSettingState.success,
    callGetTaxList,
    resetdDeleteTaxSettingState,
  ]);

  return (
    <React.Fragment>
      {(getByIdTaxSettingState.inProgress ||
        addUpdateTaxSettingState.inProgress ||
        deleteTaxSettingState.inProgress ||
        taxSettingState.inProgress) && (
        <FormLoader
          loading={
            getByIdTaxSettingState.inProgress ||
            addUpdateTaxSettingState.inProgress ||
            deleteTaxSettingState.inProgress ||
            taxSettingState.inProgress
          }
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="delete_tax"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deleteTaxSettingState.inProgress}
          message="delete_tax_confirm"
        />
      )}

      <AddTaxSettingForm
        submitHandler={onSubmitForm}
        addUpdateInProgress={addUpdateTaxSettingState.inProgress}
        actionSuccess={addUpdateTaxSettingState.success}
        initialValues={initialValues}
        id={editableId}
        onCancelEdit={() => {
          setEditableId(-1);
          resetAddUpdateTaxSettingState();
        }}
      />
      <TaxListView
        setVisiblity={handleVisibility}
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        onPageLimitChanged={onPageLimitChanged}
        pageLimit={pageLimit}
        pageInfo={pageInfo}
        onPageChanged={onPageChanged}
        disablePagination={disablePagination}
        sorting={sorting}
        handleSortingChange={handleSortingChange}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
        setIsRefreshPage={setIsRefreshPage}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(TaxSettings));
